// Single source of truth for site copy, imagery and pricing.
// Update BRAND here to rename the company everywhere.
export const BRAND = 'RIME';
export const BRAND_TAGLINE = 'Bookkeeping + Payroll';

export const contact = {
  email: 'contact@hellorime.com',
  phone: '(555) 000-0000',
  phoneHref: 'tel:+15550000000',
  scheduling: 'calendly.com/hellorime',
  schedulingHref: 'https://calendly.com/hellorime',
  location: 'Serving clients nationwide',
};

export const navLinks = [
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'Industries', href: '#industries' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Contact', href: '#contact' },
];

export const footerLinks = [
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Contact', href: '#contact' },
];

export const aboutCredentials = [
  {
    name: 'QuickBooks Online ProAdvisor Certified',
    src: '/images/logos/quickbooks-proadvisor.png',
  },
  {
    name: 'Intuit Bookkeeping Professional Certificate',
    src: '/images/logos/intuit-bookkeeping.png',
  },
];

export const platformLogos = [
  { name: 'ADP', src: '/images/logos/adp.png' },
  { name: 'Paychex', src: '/images/logos/paychex.png' },
];

export const images = {
  logo: '/images/brand/rime-lockup.png',
  logoOnDark: '/images/brand/rime-lockup-on-dark.png',
  mark: '/images/brand/rime-mark.png',
  hero: '/images/rime-hero.jpg',
  contact:
    'https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=1400&q=80',
  founder: '/images/dinar-davis.png',
};

export const services = [
  {
    id: 'bookkeeping',
    title: 'Bookkeeping',
    tagline: 'Stay current.',
    items: [
      'Transaction categorization',
      'Bank and credit-card reconciliation',
      'Monthly financial statements',
      'Profit & Loss and Balance Sheet',
      'Consistent monthly close',
    ],
  },
  {
    id: 'cleanup',
    title: 'Cleanup + Catch-Up',
    tagline: 'Get current.',
    items: [
      'Review and cleanup of existing books',
      'Account reconciliation',
      'Transaction corrections',
      'Resolution of outstanding discrepancies',
      'Clean starting point for ongoing bookkeeping',
    ],
  },
  {
    id: 'setup',
    title: 'QuickBooks Setup',
    tagline: 'Start clean.',
    items: [
      'QuickBooks Online setup',
      'Customized chart of accounts',
      'Bank and credit-card connections',
      'Opening balances',
      'Account walkthrough',
    ],
  },
  {
    id: 'payroll',
    title: 'Payroll',
    tagline: 'Pay people right.',
    items: [
      'Regular payroll processing',
      'Employee additions and changes',
      'Earnings, deduction and withholding updates',
      'PTO and payroll adjustments',
      'Payroll reconciliation',
      'Payroll reports and recordkeeping',
      'ADP and Paychex experience',
    ],
  },
];

export const industries = [
  {
    name: 'Consultants',
    image:
      'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1200&q=80',
    description: 'Clean monthly books so client income and expenses stay clear without the admin drag.',
  },
  {
    name: 'Agencies',
    image:
      'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80',
    description: 'Accurate bookkeeping and payroll so team costs and cash flow stay under control.',
  },
  {
    name: 'Solopreneurs',
    image:
      'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=1200&q=80',
    description: 'Reliable monthly books so you always know what the business actually earned.',
  },
  {
    name: 'Nonprofits',
    image:
      'https://images.unsplash.com/photo-1559027615-cd4628902d4a?auto=format&fit=crop&w=1200&q=80',
    description: 'Organized bookkeeping and payroll that keep programs and people paid on time.',
  },
  {
    name: 'Service Businesses',
    image:
      'https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=1200&q=80',
    description: 'Steady bookkeeping and payroll so busy seasons don’t bury the numbers.',
  },
];

export const projectPlans = [
  {
    id: 'setup',
    name: 'QuickBooks Setup',
    price: '$250',
    unit: 'one-time',
    copy: 'Get your books set up correctly from the start.',
    items: [
      'QuickBooks Online setup',
      'Customized chart of accounts',
      'Bank and credit-card feed connections',
      'Opening balances',
      '30-minute walkthrough',
    ],
  },
  {
    id: 'cleanup',
    name: 'Cleanup + Catch-Up',
    price: '$100',
    unit: '× each month behind',
    copy: 'Get overdue or disorganized books back in order.',
    items: [
      'Review of existing books',
      'Transaction cleanup',
      'Account reconciliation',
      'Corrections and adjustments',
      'Clean financial statements',
    ],
    note: "Whether you're a few months behind or starting fresh after a long stretch, we'll get your books caught up and ready to move forward.",
  },
];

export const monthlyPlans = {
  essential: {
    id: 'essential',
    name: 'Essential',
    price: '$300',
    unit: '/month',
    copy: 'Straightforward monthly bookkeeping, handled.',
    items: [
      'Monthly transaction categorization',
      'Bank and credit-card reconciliation',
      'Profit & Loss statement',
      'Balance Sheet',
      'Monthly financial close',
      'Financials delivered by the 10th',
    ],
  },
  growth: {
    id: 'growth',
    name: 'Growth',
    price: '$450',
    unit: '/month',
    copy: 'For businesses with more financial activity and ongoing bookkeeping needs.',
    items: [
      'Everything in Essential',
      'Higher-volume monthly bookkeeping',
      'Basic accounts receivable or accounts payable tracking',
    ],
    note: "Have a higher-volume or more complex business? We'll provide a simple custom quote.",
  },
};

export const payrollTiers = [
  { min: 1, max: 5, price: '$150' },
  { min: 6, max: 10, price: '$200' },
  { min: 11, max: 20, price: '$275' },
  { min: 21, max: 30, price: '$350' },
  { min: 31, max: 40, price: '$425' },
  { min: 41, max: 50, price: '$500' },
];

export const payrollIncludes = [
  'Regular payroll processing',
  'Employee additions and changes',
  'Earnings, deductions and withholding updates',
  'PTO and payroll adjustments',
  'Payroll reconciliation',
  'Payroll reports and recordkeeping',
  'ADP and Paychex experience',
];

export const faqs = [
  {
    question: 'Why hire a bookkeeper if I already use QuickBooks?',
    answer:
      'QuickBooks is a powerful tool, but it still needs someone to keep the information accurate. We handle categorization, reconciliation, month-end review and financial reporting so your books stay current and you can trust what you’re looking at.',
  },
  {
    question: 'What exactly do you handle every month?',
    answer:
      'Depending on your plan, we categorize transactions, reconcile bank and credit-card accounts, track basic receivables or payables, review the books for discrepancies, and deliver your monthly Profit & Loss and Balance Sheet.',
  },
  {
    question: 'What if my books are already behind?',
    answer:
      'That’s what Cleanup + Catch-Up is for. Whether you’re a few months behind or starting fresh after a long stretch, we’ll get your books caught up and ready to move forward. Pricing is $100 for each month behind.',
  },
  {
    question: 'Do you prepare taxes?',
    answer:
      'We focus on bookkeeping rather than tax preparation. We’ll keep your books organized and tax-ready and can work directly with your CPA or tax preparer when it’s time to file.',
  },
  {
    question: 'Will you work with my CPA or tax preparer?',
    answer:
      'Absolutely. Good bookkeeping should make your accountant’s job easier. We can coordinate with your tax professional and provide the reports and bookkeeping information they need.',
  },
  {
    question: 'Do you offer payroll?',
    answer:
      'Yes. Managed payroll processing is available as a standalone service for businesses using platforms including ADP and Paychex. Pricing is a simple monthly fee based on the size of your team.',
  },
  {
    question: 'How is my financial information protected?',
    answer:
      'Account access is protected with multi-factor authentication, and sensitive documents are exchanged securely rather than through ordinary email. Whenever possible, we use accountant or authorized-user access rather than asking clients to share passwords.',
  },
  {
    question: 'Do I have to sign a long-term contract?',
    answer: 'No. Monthly bookkeeping services can be canceled with 30 days’ notice.',
  },
];
